import type { ReactNode } from 'react';
import { observer } from 'mobx-react-lite';

import { EditString } from 'components/forms/EditString';
import { EditText } from 'components/forms/EditText';
import { EditFolderPath } from 'edit/reference/EditFolderPath';
import { EditZoneReference } from 'edit/reference/EditZoneReference';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';

interface Props {
  readonly entity: EditableNodeDefinition;
  readonly isEditMode: boolean;
  /** The type-specific graph/config editor (Flow, Bash, …), still on the state/setState bridge. */
  readonly children: ReactNode;
}

/**
 * The Definition tab: the node's identity + placement (name / zone / folder / description) bound straight to the
 * `EditableNodeDefinition` model, over the still-bridged type-specific editor injected as `children`.
 */
export const DefinitionTab = observer<Props>(({ entity, isEditMode, children }) => (
  <div className="definition-tab-body">
    <div className="mb-2">
      <EditString label="Name" value={entity.name} onChange={(value) => (entity.name = value)} />
    </div>

    <div className="d-flex gap-3 mb-2">
      <div className="flex-grow-1" style={{ flexBasis: 0, minWidth: 0 }}>
        <EditZoneReference
          value={entity.zoneName ? { name: entity.zoneName } : null}
          onChange={(zone) => (entity.zoneName = zone?.name ?? null)}
          resourceType="node_definitions"
          action={isEditMode ? 'update' : 'create'}
        />
      </div>
      <div className="flex-grow-1" style={{ flexBasis: 0, minWidth: 0 }}>
        <EditFolderPath value={entity.folderPath} onChange={(value) => (entity.folderPath = value)} zoneName={entity.zoneName} />
      </div>
    </div>

    <div className="mb-2">
      <EditText label="Description" value={entity.description} onChange={(value) => (entity.description = value)} />
    </div>

    {children}
  </div>
));
